<template>
	<div :class="['date-input', { disabled: disabled, error: hasError }]">
		<label>
			<h5>{{ label }} <span v-if="required">*</span></h5>
		</label>
		<date-picker
			ref="datepicker"
			:value="date"
			class="date-picker"
			:format="'MM-dd-yyyy'"
			:disabled="disabled"
			:disabled-dates="{ to: minDate, from: maxDate }"
			:open-date="dateToOpen"
			:placeholder="placeholder"
			:name="name"
			@selected="onDateSelected($event)"
		>
			<template v-slot:afterDateInput>
				<svgicon name="date-picker" @click.native="openCalendar"></svgicon>
			</template>
		</date-picker>
		<div v-show="hasError" class="error-msg">{{ error }}</div>
	</div>
</template>
<script>
import '@/components/icons/date-picker';
import Datepicker from 'vuejs-datepicker';

export default {
	components: {
		datePicker: Datepicker
	},
	props: {
		label: {
			type: String,
			default: ''
		},
		required: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		value: {
			type: Date,
			default: ''
		},
		minDate: {
			type: Date,
			default: null
		},
		maxDate: {
			type: Date,
			default: null
		},
		placeholder: {
			type: String,
			default: ''
		},
		name: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			error: null,
			date: this.value
		};
	},
	computed: {
		hasError() {
			return !!this.error;
		},
		dateToOpen() {
			if (this.minDate) {
				const dateToOpen = new Date(this.minDate.getTime());

				dateToOpen.setDate(dateToOpen.getDate() + 1);
				return dateToOpen;
			}

			return null;
		}
	},
	watch: {
		value(newValue) {
			if (newValue != null) {
				this.date = newValue;
			} else {
				this.$refs.datepicker.clearDate();
			}
		}
	},
	mounted() {
		this.$refs.datepicker.$el
			.querySelector('input')
			.addEventListener('blur', this.checkForValue.bind(this));
	},
	beforeDestroy() {
		this.$refs.datepicker.$el
			.querySelector('input')
			.removeEventListener('blur', this.checkForValue.bind(this));
	},
	methods: {
		onDateSelected(date) {
			this.error = null;
			this.$emit('input', date);
		},
		openCalendar() {
			this.$refs.datepicker.showCalendar();
			this.$refs.datepicker.$el.querySelector('input').focus();
		},
		checkForValue() {
			if (!this.date) {
				this.error = this.$t('requiredInputError', { field: this.label });
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.date-input {
	position: relative;
	width: 100%;

	label {
		h5 {
			color: $font-dark;
			margin-bottom: 4px;
			span {
				color: $requiredFieldAsterisk;
			}
		}
	}
	.date-picker {
		width: 100%;

		::v-deep input {
			height: 48px;
			width: 100%;
			border: 1px solid $container-border;
			border-radius: 4px;
			outline: 0;
			color: $input-color;
			font-family: 'RobotoRegular';
			font-size: 15px;
			color: $font-header;
			text-indent: 16px;
			&:disabled {
				color: $disabled-text-color;
				background-color: $disabled-state;
				&:hover {
					cursor: not-allowed;
				}
			}
			&:not(:disabled) {
				&:hover {
					cursor: pointer;
				}
			}

			@media (min-width: $breakpoint-mid) {
				font-size: 18px;
			}
		}

		::v-deep .vdp-datepicker__calendar {
			border: 1px solid $container-border;
			border-radius: 4px;
			font-family: 'RobotoRegular';
			box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.24);

			header {
				.day__month_btn {
					&:hover {
						background-color: #ffffff;
					}
				}
				.prev {
					&:after {
						border-right-color: $primary-state;
					}
					&:hover {
						background-color: #ffffff;
						&:after {
							border-right-color: $hover-state;
						}
					}
				}
				.next {
					&:after {
						border-left-color: $primary-state;
					}
					&:hover {
						background-color: #ffffff;
						&:after {
							border-left-color: $hover-state;
						}
					}
				}
			}
			.cell:not(.blank):not(.disabled).day:hover {
				border-color: $hover-state;
				background-color: $hover-state;
				color: $font-light;
			}
			.cell {
				&.day {
					border-radius: 100%;
				}
				&.selected {
					border-color: $primary-state;
					background-color: $primary-state;
					color: $font-light;

					&:hover {
						border-color: $hover-state;
						background-color: $hover-state;
					}
				}
			}
		}
	}
	&:not(.disabled) {
		&:hover {
			cursor: pointer;

			svg {
				fill: $hover-state;
			}
		}
	}
	&.disabled {
		svg {
			fill: $font-gray;
		}
		&:hover {
			cursor: not-allowed;
		}
	}

	svg {
		position: absolute;
		height: 22px;
		right: 10px;
		bottom: 13px;
		fill: $primary-state;
	}
	&.error {
		::v-deep input {
			border-color: $error-state;
		}

		label {
			h5 {
				color: $error-state;
			}
		}
	}
	.error-msg {
		font-family: 'RobotoRegular';
		padding-top: 8px;
		line-height: 22px;
		color: $error-state;
		font-size: 14px;
		@media (min-width: $breakpoint-mid) {
			font-size: 16px;
		}
	}
}
</style>
